import clsx from 'clsx';
import { format } from 'date-fns';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MarketplaceSquareLogo } from 'src/components/common/MarketplaceSquareLogo';
import { BoostIconDisplay } from 'src/components/SponsoredListings';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext/ModalContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { Button, Card, Stack } from 'src/core/ui';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { NoData } from 'src/tables/Table';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { getEventPerformerVenue } from 'src/utils/eventWithDataUtils';
import { getTimeDateDifferenceAsShorthand } from 'src/utils/numberFormatter';
import {
  getSaleDetailsModalConfigWithDeepLink,
  getSaleStatusDisplayContentId,
} from 'src/utils/saleUtils';
import {
  Event,
  ListingDetailDataField,
  Performer,
  Permission,
  Sale,
  SaleDetails,
  Venue,
} from 'src/WebApiController';

import * as styles from './RecentSalesTilesView.css';

type SaleWithEventData = {
  sale: Sale;
  event: Event;
  performer?: Performer | null;
  venue?: Venue | null;
};

type Props = {
  data: Sale[];
  newRows: number[];
};

const TILES_PAGE_SIZE = 100;

/**
 * Parses a sale date string into a Date object, ensuring UTC interpretation.
 * The API may return dates without a timezone designator (e.g. "2026-07-22T10:00:00"),
 * which browsers interpret as local time — causing "in Xh" instead of "Xh ago".
 * Appending 'Z' forces UTC interpretation when no timezone info is present.
 */
function parseSaleDateAsUtc(dateStr: string): Date {
  // Has timezone info if it ends with Z or a ±HH:MM offset
  if (/Z$|[+-]\d{2}:\d{2}$/.test(dateStr)) {
    return new Date(dateStr);
  }
  return new Date(dateStr + 'Z');
}

function SaleTile({
  saleWithEvent,
  isNew,
}: {
  saleWithEvent: SaleWithEventData;
  isNew: boolean;
}) {
  const { sale, event, venue } = saleWithEvent;
  const { setActivePosEntity } = useActivePosEntityContext<SaleDetails>();
  const { setModal } = useContext(ModalContext);
  const hasAdPlatformViewPermission = useUserHasAnyOfPermissions(
    Permission.AdPlatform_CampaignsViewAll
  );
  const isBoostedSale = hasAdPlatformViewPermission && !!sale.adCmpFlightId;

  const onTileClick = useCallback(() => {
    setActivePosEntity(sale.id, sale.idOnMkp, true, [
      ListingDetailDataField.Basic,
    ]);
    setModal(getSaleDetailsModalConfigWithDeepLink(sale.id, true));
  }, [sale.id, sale.idOnMkp, setActivePosEntity, setModal]);

  const eventName = event.shortName || event.name;

  const formattedDate = useMemo(() => {
    if (!event.dates?.start) return null;
    return format(new Date(event.dates.start), 'EEE, MMM d, yyyy · h:mm a');
  }, [event.dates?.start]);

  const seatingDisplay = useMemo(() => {
    const parts: string[] = [];
    if (sale.seating?.section) {
      parts.push(`Sec ${sale.seating.section}`);
    }
    if (sale.seating?.row) {
      parts.push(`Row ${sale.seating.row}`);
    }
    return parts.join(', ') || '—';
  }, [sale.seating?.section, sale.seating?.row]);

  const timeAgo = useMemo(
    () =>
      getTimeDateDifferenceAsShorthand({
        inputDate: sale.saleDate ? parseSaleDateAsUtc(sale.saleDate) : null,
      }),
    [sale.saleDate]
  );

  const statusContentId = getSaleStatusDisplayContentId(
    sale.status,
    sale.mkpContrState,
    sale.mkp
  );

  return (
    <Card
      className={clsx(styles.tile, { [styles.newRow]: isNew })}
      role="button"
      tabIndex={0}
      onClick={onTileClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onTileClick();
        }
      }}
    >
      <div className={styles.tileHeader}>
        <div className={styles.tileEventName} title={eventName}>
          {eventName}
        </div>
        <div className={styles.tileHeaderRight}>
          <MarketplaceSquareLogo marketplace={sale.mkp} size={20} />
          {isBoostedSale && <BoostIconDisplay size={vars.iconSize.m} />}
          {timeAgo && <span className={styles.tileTimeAgo}>{timeAgo}</span>}
        </div>
      </div>
      {formattedDate && (
        <div className={styles.tileEventDate}>
          {formattedDate}
          {venue && ` · ${venue.name}`}
        </div>
      )}
      <div className={styles.tileMetricsRow}>
        <div className={styles.tileMetric}>
          <div className={styles.tileMetricLabel}>
            <Content id={ContentId.Seating} />
          </div>
          <div className={styles.tileMetricValue}>{seatingDisplay}</div>
        </div>
        <div className={styles.tileMetric}>
          <div className={styles.tileMetricLabel}>
            <Content id={ContentId.Quantity} />
          </div>
          <div className={styles.tileMetricValue}>{sale.qtySold}</div>
        </div>
        <div className={styles.tileMetric}>
          <div className={styles.tileMetricLabel}>
            <Content id={ContentId.Proceeds} />
          </div>
          <div className={styles.tileMetricValue}>
            {sale.ttlNetProcs?.disp ?? '—'}
          </div>
        </div>
        <div className={styles.tileMetric}>
          <div className={styles.tileMetricLabel}>
            <Content id={ContentId.Status} />
          </div>
          <div className={styles.tileMetricValue}>
            {statusContentId ? <Content id={statusContentId} /> : '—'}
          </div>
        </div>
      </div>
    </Card>
  );
}

export const RecentSalesTilesView = ({ data, newRows }: Props) => {
  const { data: catalog, isLoading: isCatalogLoading } =
    useCatalogDataContext();

  const salesText = useContent(ContentId.Sales);
  const [page, setPage] = useState(0);

  const salesWithEvents = useMemo<SaleWithEventData[]>(
    () =>
      data
        .map((sale) => {
          const { event, performer, venue } = getEventPerformerVenue(
            sale.viagVirtId,
            catalog
          );
          if (event) {
            return {
              sale,
              event: event.event,
              performer,
              venue,
            } as SaleWithEventData;
          }
          return null;
        })
        .filter((s): s is SaleWithEventData => s != null),
    [catalog, data]
  );

  const totalPages = useMemo(
    () => Math.ceil(salesWithEvents.length / TILES_PAGE_SIZE),
    [salesWithEvents.length]
  );

  // Reset to a valid page when data refreshes and current page is out of bounds
  useEffect(() => {
    if (page >= totalPages && totalPages > 0) {
      setPage(totalPages - 1);
    }
  }, [totalPages, page]);
  const pagedSales = useMemo(
    () =>
      salesWithEvents.slice(
        page * TILES_PAGE_SIZE,
        (page + 1) * TILES_PAGE_SIZE
      ),
    [salesWithEvents, page]
  );

  if (isCatalogLoading) {
    return <PosSpinner />;
  }

  if (!salesWithEvents.length) {
    return (
      <NoData>
        <FormatContent
          id={FormatContentId.NoDataAvailable}
          params={salesText}
        />
      </NoData>
    );
  }

  return (
    <div className={styles.gridScrollWrapper}>
      <div className={styles.grid}>
        {pagedSales.map(({ sale, event, performer, venue }) => (
          <SaleTile
            key={sale.id}
            saleWithEvent={{ sale, event, performer, venue }}
            isNew={newRows.includes(sale.id)}
          />
        ))}
      </div>
      {totalPages > 1 && (
        <div className={styles.paginationContainer}>
          <Button
            variant="outline"
            className={styles.paginationButton}
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            aria-label="Previous page"
          >
            ‹
          </Button>
          <span className={styles.paginationInfo}>
            {page + 1} / {totalPages}
          </span>
          <Button
            variant="outline"
            className={styles.paginationButton}
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            aria-label="Next page"
          >
            ›
          </Button>
        </div>
      )}
      <Stack className={styles.ctaContainer}>
        <Link to="/sales">
          <Button variant="outline" className={styles.cta}>
            <Content id={ContentId.ViewAllSales} />
          </Button>
        </Link>
      </Stack>
    </div>
  );
};
